import type { ReactNode } from 'react';
import { Container } from '@platform/ui';
import type { BoardConfig, Post } from '../types/board.type.ts';

interface BoardHeroProps {
  config: BoardConfig<Post>;
  totalCount: number;
  searchValue: string;
  onSearchChange: (value: string) => void;
  action?: ReactNode;
  /** 검색·작성 줄 바로 아래 오른쪽에 붙는 도구 (자료실의 페이지당 개수) */
  subAction?: ReactNode;
  /** 아래 구분선. 바로 밑에 탭이 붙는 게시판(자료실)은 탭 테두리가 대신한다 */
  divider?: boolean;
}

export function BoardHero({
  config,
  totalCount,
  searchValue,
  onSearchChange,
  action,
  subAction,
  divider = true,
}: BoardHeroProps) {
  return <section className={`relative bg-white dark:bg-dark-card ${divider ? 'border-b border-black/[0.08] dark:border-white/10' : ''}`}>
    <Container>
      {/* 아래 여백은 검색·작성 줄과 subAction 사이(mt-3)와 같게 — 탭이 hero 에 붙어 보인다 */}
      <div className="pt-[120px] pb-3 sm:pt-30">
        <span className="text-[12px] font-semibold uppercase tracking-[0.12em] text-neutral-950/40 dark:text-white/40">
          {config.type.toUpperCase()}
        </span>
        <h1 className="mt-3 text-[clamp(40px,5.5vw,72px)] font-extrabold leading-[1.02] -tracking-[0.05em]">
          {config.title}
        </h1>
        <p className="mt-4 max-w-[680px] text-[16px] leading-[1.6] text-neutral-950/60 dark:text-white/60 sm:text-[17px]">
          {config.description}
        </p>

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <span className="text-[13px] font-medium text-neutral-950/55 dark:text-white/55">
            총{' '}<strong className="text-neutral-950 dark:text-neutral-100">{totalCount.toLocaleString('ko-KR')}</strong>건
          </span>

          <div className="ml-auto flex w-full min-w-0 items-center gap-2 sm:w-auto">
            <div className="relative flex min-w-0 flex-1 items-center sm:w-[360px] sm:flex-none">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                className="pointer-events-none absolute left-4 text-neutral-950/40 dark:text-white/40"
                aria-hidden
              >
                <circle
                  cx="7"
                  cy="7"
                  r="5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  fill="none"
                />
                <path
                  d="M11 11L14 14"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
              <input
                type="search"
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="제목·내용 검색"
                className="h-11 w-full rounded-full border border-black/15 dark:border-white/15 bg-white dark:bg-dark-card pl-11 pr-4 text-[14px] outline-none transition-colors placeholder:text-neutral-950/35 dark:placeholder:text-white/35 focus:border-neutral-950"
              />
            </div>
            {action && <div className="shrink-0">{action}</div>}
          </div>
        </div>
        {subAction && <div className="mt-3 flex justify-end">{subAction}</div>}
      </div>
    </Container>
  </section>
}
